import {
  useQuery,
  UseQueryOptions,
  QueryKey,
  UseQueryResult,
  QueryFunctionContext,
} from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { selectAuthToken } from 'AuthModule/selectors';
import { SetRequired } from 'type-fest';
import { useCurrentContext } from 'hooks/useCurrentContext';
import ApiClientManager from 'core/apiClient';

/**
 * Context provided to query functions by useAppQuery.
 * Includes auth token and URL-based tenant/realm context.
 */
export interface AppQueryContext {
  token: string;
  /** Current tenant from URL params or auth default */
  tenant: string | undefined;
  /** Current realm ID from URL params or auth default */
  realmId: string | undefined;
  /** Pre-built headers with x-tenant and x-realm-id for API calls */
  contextHeaders: Record<string, string>;
  /** Build API config with context headers merged in */
  buildConfig: (additionalConfig?: ApiConfig) => ApiConfig;
}

type UseAppQueryParams<
  SuccessResponseType,
  ErrorResponseType,
  QueryKeyType extends QueryKey,
> = Omit<
  SetRequired<
    UseQueryOptions<SuccessResponseType, ErrorResponseType, SuccessResponseType, QueryKeyType>,
    'queryKey'
  >,
  'queryFn'
> & {
  queryFn: (args: QueryFunctionContext & AppQueryContext) => Promise<SuccessResponseType>;
};

/**
 * Wrap useQuery with auth token and URL context injection.
 *
 * All query functions receive:
 * - token: Auth token for Authorization header
 * - tenant: Current tenant from URL or auth default
 * - realmId: Current realm from URL or auth default
 * - contextHeaders: Pre-built x-tenant and x-realm-id headers
 * - buildConfig: Helper to merge context headers into API config
 *
 * @example
 * ```tsx
 * useAppQuery({
 *   queryKey: ['myQuery'],
 *   queryFn: ({ token, buildConfig }) => {
 *     return myApi({ params, token, config: buildConfig() });
 *   },
 * });
 * ```
 */
export const useAppQuery = <
  SuccessResponseType,
  ErrorResponseType = unknown,
  QueryKeyType extends QueryKey = QueryKey,
>(
  options: UseAppQueryParams<SuccessResponseType, ErrorResponseType, QueryKeyType>,
): UseQueryResult<SuccessResponseType, ErrorResponseType> => {
  const token = useSelector(selectAuthToken);
  const { tenant, realmId } = useCurrentContext();
  const { queryFn, enabled, ...rest } = options;

  // Build context headers from URL params (or auth defaults)
  const contextHeaders = ApiClientManager.buildUrlHeaders({
    tenantName: tenant,
    realmId,
  });

  // Helper to build API config with context headers
  const buildConfig = (additionalConfig?: ApiConfig): ApiConfig => ({
    ...additionalConfig,
    headers: {
      ...contextHeaders,
      ...additionalConfig?.headers,
    },
  });

  return useQuery({
    ...rest,
    // Disable the query if there's no token or if explicitly disabled
    enabled: !!token && (enabled !== undefined ? enabled : true),
    queryFn: (args: QueryFunctionContext) => {
      // TypeScript guard: token is guaranteed to be a string when query is enabled
      return queryFn({
        ...args,
        token: token as string,
        tenant,
        realmId,
        contextHeaders,
        buildConfig,
      });
    },
  } as unknown as UseQueryOptions<
    SuccessResponseType,
    ErrorResponseType,
    SuccessResponseType,
    QueryKeyType
  >);
};
